import {
  useEffect, useMemo, useCallback, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, message } from 'antd';
import {
 useParams, useHistory, useRouteMatch, useLocation,
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { findRouteWithKey } from '@routes/routes';
import devicesAlarmApi from '@api/devicesAlarmApi';
import devicesActions from '@state/device/actions';
import validator from 'validator';

import variablesActions from '@state/variables/actions';
import devicesApi from '@api/devicesApi';

function useDevicesEditCreate() {

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();

  const { alarm_id } = useParams();
  const [form] = Form.useForm();

  const createEditValues = useSelector(({ device: a }) => a.createEditValues);
  const isEditDefault = useSelector(({ device: a }) => a.isEditDefault);

  const [container, setContainer] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isForCreate] = useState(!alarm_id);
  const [loadingAlarmToEdit, setLoadingAlarmToEdit] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const currentUserRol = useSelector(({ user }) => user.role);
  const [businessData, setBusinessData] = useState([]);

  const abortC = new AbortController();
  const { signal } = abortC;

  const pathBefore = useMemo(() => {

    const { path: fromDefaults } = findRouteWithKey('RULES__DEVICE_DEFAULT');
    const { path: fromDynamic } = findRouteWithKey('RULES__DEVICE');

    const paths = [fromDefaults, fromDynamic];
    return paths.find((path) => match.path.includes(path));

  }, [match]);

  const breadcrumbData = useMemo(() => {

    const currentSection = isForCreate
      ? t('views.dynamic_alarms_create_edit.breadcrumb.create_gateway')
      : `${t('views.dynamic_alarms_create_edit.breadcrumb.edit_gateway')} / n° ${alarm_id}`;

    const { path: fromDefaults } = findRouteWithKey('RULES__DEVICE_DEFAULT');

    if (match.path.includes(fromDefaults)) {

      return [
        [fromDefaults, t('views.dynamic_alarms_create_edit.breadcrumb.gateway_rules')],
        [null, currentSection],
      ];

    }

    const { path: fromDynamic } = findRouteWithKey('RULES__DEVICE');
    return [
      [fromDynamic, t('views.dynamic_alarms_create_edit.breadcrumb.gateway_rules')],
      [null, currentSection],
    ];

  }, [isForCreate, alarm_id, match, t]);

  const initialForm = useMemo(() => ({
    name: '',
    // origin: '',
    // description: null,
    // severity: 'Critical',
    is_active: true,
    conditions: [],
    actions: [],
  }), []);

  const formOnValuesChange = useCallback(
    (changedValues) => {

      const newEditValues = { ...createEditValues };

      Object.entries(changedValues).forEach(([key, value]) => {

        newEditValues[key] = value;

      });

      dispatch(devicesActions.setCreateEditValues(newEditValues));

    },
    // eslint-disable-next-line
    [createEditValues]
  );

  const isFirstStepValid = useCallback(async () => {

    try {

      await form.validateFields();
      return true;

    } catch {

      return false;

    }

  }, [form]);

  const isSecondStepValid = useCallback(() => {

    // Verificar que al menos haya un grupo
    const { conditions } = createEditValues || {};
    if (!conditions || conditions.length === 0) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.add_at_least_one_condition'));
      return false;

    }

    // Verificar que los grupos tengan al menos una condición
    const filtersEmptyGroup = conditions.filter((orConditions) => orConditions.length === 0);
    if (filtersEmptyGroup.length > 0) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.group_without_condition'));
      return false;

    }

    // Verificar que las orConditions este completas
    const allOrConditions = conditions.flat();

    let isValidAllOrConditions = true;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < allOrConditions.length; i++) {

      const orCondition = allOrConditions[i];
      const {
        type, // params, values,
      } = orCondition;

      if (!type) {

        isValidAllOrConditions = false;
        break;

      }

    }

    if (!isValidAllOrConditions) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.completed_conditions'));
      return false;

    }

    return true;

  }, [createEditValues, t]);

  const isStepValidate = useCallback(async () => {

    switch (currentStep) {

      case 0:
        // eslint-disable-next-line no-return-await
        return await isFirstStepValid();
      case 1:
        return isSecondStepValid();
      default:
        return false;

    }
    // eslint-disable-next-line
  }, [currentStep, createEditValues]);

  const onPrevious = useCallback(() => {

    setCurrentStep(currentStep - 1);

  }, [currentStep]);

  const onNext = useCallback(async () => {

    const isStepValid = await isStepValidate();
    if (isStepValid) setCurrentStep(currentStep + 1);
    // eslint-disable-next-line
  }, [currentStep, isStepValidate]);

  const onSave = useCallback(() => {

    const { actions } = createEditValues || {};

    const allUrls = [];
    const allEmails = [];

    actions.forEach((action) => {

      const { type, params } = action;
      switch (type) {

        case 'webhook':
          allUrls.push(...allUrls, ...params.urls);
          break;

        case 'email':
          allEmails.push(...allEmails, ...params.recipients);
          break;

        default:
          break;

      }

    });

    console.log('actions: ', actions);
    // Check actions
    if (actions.length < 1) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.add_action'));
      return;

    }

    // Check urls
    if (allUrls.some((url) => url.length === 0)) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.delete_empty_fields_url'));
      return;

    }

    console.log('allEmails: ', allEmails);
    // Check emails
    if (allEmails.some((email) => email.length === 0)) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.delete_empty_fields_email'));
      return;

    }
    // Check emails validate
    if (!allEmails.every((email) => validator.isEmail(email))) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.error_fields_email'));
      return;

    }

    const {
      id, created_at, is_active, updated_at,
    } = createEditValues;
    const body = createEditValues;
    if (id) delete body.id;
    if (created_at) delete body.created_at;
    if (is_active) delete body.is_active;
    if (updated_at) delete body.updated_at;

    setSaveLoading(true);

    const new_body = { ...body };
    const array = [];
    new_body.conditions[0].forEach((condition) => { // sacando condiciones

      array.push(condition.type);

    });
    const action_array = [];
    new_body.actions.forEach((data) => { // sacando acciones

      const action = {
        type: data.type,
        recipients: (data.params.recipients) ? data.params.recipients : [data.params.urls],
      };
      action_array.push(action);

    });
    if (new_body.conditions) delete new_body.conditions;
    new_body.bits_error = array;
    // new_body.conditions = array;
    new_body.actions = action_array;
    new_body.origin = 'Inst-Telemetry-Solar';
    devicesAlarmApi
      .createUpdateAlarm({ id, body: new_body })
      .then((response) => {

        // eslint-disable-next-line no-shadow
        const array = [];
        response.bits_error.forEach((condition) => { // create bits

          array.push({ type: condition });

        });
        // eslint-disable-next-line no-shadow
        const action_array = [];
        response.actions.forEach((data) => {

          const action = {
            type: data.type,
            params: {
              recipients: [data.recipients],
            },
          };
          action_array.push(action);

        });
        response.conditions = [array];
        response.actions = action_array;

        setSaveLoading(false);

        if (isEditDefault) {

          dispatch(variablesActions.setDefaultAlarms(null));
          history.replace(pathBefore || '/reglas/dispositivos');
          message.success(t('views.dynamic_alarms_create_edit.feedbacks.created_alarm_devices'));

        }

        if (!id) {

          dispatch(devicesActions.addAlarmToListOrSumCount(response));
          history.replace(pathBefore || '/reglas/dispositivos');
          message.success(t('views.dynamic_alarms_create_edit.feedbacks.created_alarm_devices'));
          return;

        }

        dispatch(devicesActions.editAlarm(response));
        history.push(pathBefore || '/reglas/dispositivos');
        message.success(t('views.dynamic_alarms_create_edit.feedbacks.updated_alarm_devices'));

      })
      .catch((resError) => {

        // eslint-disable-next-line no-console
        console.log('error', resError.data.detail[0].msg);
        message.error(resError.data.detail[0].msg);
        setSaveLoading(false);
        const {
          // eslint-disable-next-line no-shadow
          data: { actions },
          fem,
        } = resError;
        if (actions && typeof actions === 'object') {

          if ('urls' in actions) {

            message.error(t('views.dynamic_alarms_create_edit.feedbacks.verify_urls'));
            return;

          }

        }
        message.error('error-015');
        // eslint-disable-next-line no-console
        console.log(t(fem));

      });

  }, [createEditValues, pathBefore, isEditDefault, t, dispatch, history]);

  const readBusinessType = useCallback(async () => {

    const skip = 0;
    const limit = 1000;

    await devicesApi.getAllBusinessType({ signal, skip, limit })
      .then(async (business) => {

          const newBusiness = await Promise.all(business.map(async (obj) => {

            const item = {
              value: obj.id,
              label: obj.name,
              devices: obj.total_general,
              clients: obj.total_general_clients,
              type: obj.type,
            };
            return item;

}));

          setBusinessData(newBusiness);

      }).catch((resError) => {

          if (signal.aborted) return;
          const { fem } = resError;
          console.log('fem ', fem);

      });

  });

  // eslint-disable-next-line consistent-return
  useEffect(() => {

    dispatch(devicesActions.setIsEditDefault({ forceIsEdit: false }));

    // use efect for resquest alarm to edit
    if (alarm_id) {

      setLoadingAlarmToEdit(true);
      devicesAlarmApi
        .getAlarmById({ id: alarm_id, signal })
        .then((response) => {

          const array = [];
          response.bits_error.forEach((condition) => { // conditions

            array.push({ type: condition });

          });
          const action_array = [];
          response.actions.forEach((data) => { // actions

            const action = {
              type: data.type,
              params: {
                recipients: data.recipients,
              },
            };
            action_array.push(action);

          });
          response.conditions = [array];
          response.actions = action_array;
          dispatch(devicesActions.setIsEditDefault(response));
          dispatch(devicesActions.setCreateEditValues(response));
          setLoadingAlarmToEdit(false);

        })
        .catch((resError) => {

          if (signal.aborted) return;
          const { fem } = resError;
          // eslint-disable-next-line no-console
          console.log(t(fem));
          setLoadingAlarmToEdit(false);

        });
      // eslint-disable-next-line
      return () => {

        return false;

      };

    }

    dispatch(devicesActions.setCreateEditValues(initialForm));

  // eslint-disable-next-line
  }, [alarm_id, dispatch, initialForm]);

  useEffect(() => {

    if (!isEditDefault) {

      setCurrentStep(0);
      return;

    }
    setCurrentStep(2);

  }, [isEditDefault]);

  // Limpia el CreateEditValues del redux al cerrar sección
  // eslint-disable-next-line
  useEffect(() => () => dispatch(devicesActions.setCreateEditValues(null)), []);

  useEffect(() => { // mount component

    readBusinessType();
    // eslint-disable-next-line
    if (!isEditDefault && location.pathname === '/reglas/dispositivos/:alarm_id') {

      // If trying to edit without isEditDefault, redirect to tipo-negocio
        history.push('/reglas/dispositivos');
// eslint-disable-next-line
} else if (!isEditDefault && currentUserRol !== 'Superadmin' && currentUserRol !== 'Admin') {
          // eslint-disable-next-line
          // If trying to create without isEditDefault and not Superadmin or Admin, redirect to tipo-negocio
          // eslint-disable-next-line
          history.push('/reglas/dispositivos');

}
    // eslint-disable-next-line
  }, []);

  return {
    breadcrumbData,
    container,
    setContainer,
    form,
    formOnValuesChange,
    createEditValues,
    loadingAlarmToEdit,
    currentStep,
    onPrevious,
    onNext,
    onSave,
    saveLoading,
    isEditDefault,
    businessData,
    alarm_id,
  };

}

export default useDevicesEditCreate;
